import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { FlowAgentState, FlowKind, FlowPricingMode } from 'flow-schema';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';

import { api, isDefined, useLoggedInAuthContext, useToast } from 'common';

import { NewFlowDialogFields } from './NewFlowDialogFields';
import { EventAction, EventCategory, useCaptureEvents } from '../../../../utils/events/index';
import { FeatureFlagKey, useFlag } from '../../../../utils/flags';
import { tryAdaptZodError, updateFormikFieldErrors } from '../../../../utils/form';
import { convertTagsToApiValue } from '../../Details/TemplatePicker/Fields/TagAutocomplete/utils';
import { CREATE_AGENT_ID } from '../../Flows/Controls/AgentIdControl';
import { FlowKindContent } from '../../Flows/flowContent';
import type { FlowCreationAction } from '../../Flows/types';

type CreateFlowFormValues = Omit<
  api.FlowDefinition.CreateFlowWithDefinitionValuesNewAgent &
    api.FlowDefinition.CreateFlowWithDefinitionValuesExistingAgent,
  'data' | 'organizationId' | 'tags'
> & {
  useCaseTag?: string;
  customTags?: string[];
  pricingMode?: FlowPricingMode;
};

interface CreateFlowDialogProps {
  isOpen: boolean;
  onClose: () => void;
  createFlowValues: Pick<api.FlowDefinition.CreateFlowWithDefinitionValues, 'name'> &
    (
      | { sourceFlowDefinitionId: string; data?: never }
      | { sourceFlowDefinitionId?: never; data: api.FlowDefinition.FlowData }
    ) & {
      documentationMarkdown?: api.FlowDefinition.CreateFlowWithDefinitionValues['documentationMarkdown'];
      useCaseTag?: string;
    };
  agentId?: string;
  organizationId?: string;
  flowCreationAction?: FlowCreationAction;
}

export function NewFlowDialog(props: CreateFlowDialogProps) {
  const { isOpen, onClose, createFlowValues, agentId, organizationId, flowCreationAction } = props;
  const { defaultOrganization, organizationsAreVisible, isAdminRole } = useLoggedInAuthContext();
  const [selectedAgentId, setSelectedAgentId] = useState<string | undefined>(agentId);
  const toast = useToast();
  const history = useHistory();
  const { captureEvent } = useCaptureEvents();
  const { showTagsOnDialog } = useFlag({
    flagKey: FeatureFlagKey.flowTagOptions,
  });
  const { isPricingModeEnabled, isUseCaseRequiredEnabled } = useFlag({
    flagKey: FeatureFlagKey.taskBasedPricing,
  });
  const validateFieldIfCreatingNewAgent = (value: string | undefined) => {
    return selectedAgentId !== CREATE_AGENT_ID || isDefined(value);
  };
  const content = FlowKindContent[FlowKind.Base];

  return (
    <Formik<CreateFlowFormValues>
      initialValues={{
        name: createFlowValues.name,
        agentId: agentId ?? null,
        kind: FlowKind.Base,
        agent: {
          name: '',
          category: api.FlowAgent.AgentCategory.Placeholder,
          organizationId: organizationsAreVisible
            ? (organizationId ?? '')
            : (defaultOrganization?.id ?? ''),
          state: FlowAgentState.Active,
        },
        ...(showTagsOnDialog && {
          useCaseTag: createFlowValues.useCaseTag,
        }),
        ...(isAdminRole &&
          isPricingModeEnabled && {
            pricingMode: FlowPricingMode.Block,
          }),
      }}
      enableReinitialize
      validationSchema={yup.object({
        name: content.metadataFields.name.yupSchema,
        agentId: yup
          .string()
          .nullable()
          .test('agent-required', 'Agent is required', (value) => {
            return selectedAgentId === CREATE_AGENT_ID || isDefined(value);
          }),
        ...(showTagsOnDialog && {
          useCaseTag:
            isAdminRole && isUseCaseRequiredEnabled
              ? yup.string().nullable().required('Use Case is required')
              : content.metadataFields.useCaseTag.yupSchema,
          customTags: content.metadataFields.customTags.yupSchema,
        }),
        agent: yup.object({
          name: yup
            .string()
            .test('agentNameRequired', 'Name is required', validateFieldIfCreatingNewAgent),
          category: yup
            .mixed<api.FlowAgent.AgentCategory>()
            .oneOf(Object.values(api.FlowAgent.AgentCategory))
            .test('agentCategoryRequired', 'Category is required', validateFieldIfCreatingNewAgent),
          organizationId: yup
            .string()
            .test(
              'agentOrganizationIdRequired',
              'Organization is required',
              validateFieldIfCreatingNewAgent
            ),
        }),
      })}
      onSubmit={async (values, helpers) => {
        try {
          let targetAgentId = values.agentId;
          let targetOrgId = organizationId;

          if (selectedAgentId === CREATE_AGENT_ID) {
            try {
              const newAgent = await api.client.FlowAgent.create({
                name: values.agent.name,
                organizationId: values.agent.organizationId,
                category: values.agent.category ?? api.FlowAgent.AgentCategory.Placeholder,
                state: FlowAgentState.Active,
              });
              targetAgentId = newAgent.id;
              targetOrgId = newAgent.organizationId;
            } catch (error) {
              updateFormikFieldErrors({
                possibleZodError: error,
                setFieldError: helpers.setFieldError,
                fieldPrefix: 'agent',
              });
            }
          } else if (!isDefined(targetOrgId) && isDefined(targetAgentId)) {
            const selectedAgent = await api.client.FlowAgent.findOneById(targetAgentId);
            targetOrgId = selectedAgent.organizationId;
          }

          if (!isDefined(targetAgentId) || !isDefined(targetOrgId)) {
            toast.showError('Failed to create new flow');
            return;
          }

          let newFlow: api.FlowDefinition.FlowDefinitionWithFlow;
          if (isDefined(createFlowValues.sourceFlowDefinitionId)) {
            newFlow = await api.client.FlowDefinition.cloneAsNewFlow({
              flowDefinitionId: createFlowValues.sourceFlowDefinitionId,
              values: {
                name: values.name,
                description: values.description ?? undefined,
                agentId: targetAgentId,
                documentationMarkdown: createFlowValues.documentationMarkdown,
                ...(showTagsOnDialog && {
                  tags: convertTagsToApiValue({
                    useCaseTag: values.useCaseTag,
                    customTags: values.customTags,
                  }),
                }),
                ...(isDefined(values.pricingMode) && {
                  pricingMode: values.pricingMode,
                }),
              },
              targetFlowKind: FlowKind.Base,
            });
          } else if (isDefined(createFlowValues.data)) {
            newFlow = await api.client.FlowDefinition.create({
              name: values.name,
              agentId: targetAgentId,
              organizationId: targetOrgId,
              kind: FlowKind.Base,
              data: createFlowValues.data,
              documentationMarkdown: createFlowValues.documentationMarkdown,
              ...(showTagsOnDialog && {
                tags: convertTagsToApiValue({
                  useCaseTag: values.useCaseTag,
                  customTags: values.customTags,
                }),
              }),
              ...(isDefined(values.pricingMode) && {
                pricingMode: values.pricingMode,
              }),
            });
          } else {
            toast.showError('Failed to create new flow');
            return;
          }

          history.push(`/agents/flows/${newFlow.flowId}/definitions/${newFlow.id}`);
          toast.showSuccess('Created new flow');

          captureEvent({
            category: EventCategory.CreateFlowDialog,
            action: EventAction.SubmitButtonClick,
            properties: {
              flowCreationAction,
            },
          });
        } catch (error) {
          const adaptedError = tryAdaptZodError(error);
          if (adaptedError) {
            for (const [field, error] of adaptedError) {
              helpers.setFieldError(field, Array.isArray(error) ? error.join(', ') : error);
            }
          } else {
            toast.showError('Failed to create new flow');
          }
        }
      }}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({ isSubmitting, resetForm }) => (
        <Dialog
          open={isOpen}
          onClose={() => {
            onClose();
            resetForm();
            setSelectedAgentId(agentId);
          }}
          maxWidth="xs"
          fullWidth
          slotProps={{ paper: { component: Form, sx: { maxHeight: '80vh' } } }}
        >
          <DialogTitle>Create flow</DialogTitle>
          <DialogContent>
            <NewFlowDialogFields
              selectedAgentId={selectedAgentId}
              onAgentChange={setSelectedAgentId}
              agentIdControlVariant={isDefined(agentId) ? 'PickAgent' : 'CreateOrPickAgent'}
              showPricingMode={isAdminRole && isPricingModeEnabled}
            />
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => {
                onClose();
                resetForm();
                setSelectedAgentId(agentId);
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button variant="contained" type="submit" loading={isSubmitting}>
              Create flow
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Formik>
  );
}
